<script lang="ts">
	import type { PriceTier } from '$lib/components/types';
	import type { DatasheetPoiScheme, MichelinDistinction } from '../../types';
	import Badge from '$lib/components/atoms/badge/Badge.svelte';
	import Image from '$lib/components/atoms/image/Image.svelte';
	import PriceRange from '$lib/components/atoms/priceRange/PriceRange.svelte';
	import Icon from '$lib/components/icons/Icon.svelte';
	import RepeatIcons from '$lib/components/icons/RepeatIcons.svelte';
	import * as m from '$paraglide/messages';
	import { cubicInOut } from 'svelte/easing';
	import { slide } from 'svelte/transition';
	import { MAX_PRICE_CATEGORY } from '../../constants';

	export let imageUrl: string | undefined = undefined;
	export let displayPicture = true;
	export let scheme: DatasheetPoiScheme;
	export let reviewText: string | undefined = undefined;
	export let michelinDistinction: MichelinDistinction | undefined = undefined;
	export let hasGreenStar: boolean = false;
	export let priceText: string | undefined = undefined;
	export let priceCategory: PriceTier | undefined = undefined;
	export let subTypeText: string | undefined = undefined;
	export let evData:
		| { spotCount: string; connectorHighlight: string; maxPower: number }
		| undefined = undefined;

	const backgroundColor: Partial<Record<DatasheetPoiScheme, string>> = {
		hotel: 'bg-hotel-weakest',
		hotelMichelin: 'bg-hotel-weakest',
		restaurant: 'bg-restaurant-weakest',
		restaurantMichelin: 'bg-restaurant-weakest',
		tourism: 'bg-tourism-weakest',
		electric: 'bg-electric-weakest',
		rest: 'bg-rest-stop-weakest',
	};

	const colorClass: Record<DatasheetPoiScheme, string> = {
		hotel: 'text-hotel',
		hotelMichelin: 'text-hotel',
		restaurant: 'text-restaurant',
		restaurantMichelin: 'text-restaurant',
		tourism: 'text-tourism',
		electric: 'text-electric',
		rest: 'text-rest-stop',
	};

	const badgeColorClasses: Record<DatasheetPoiScheme, string> = {
		hotel: '',
		hotelMichelin:
			'bg-hotel-michelin text-neutral-weakest border-hotel-michelin-strong border-1 border-solid',
		restaurant: '',
		restaurantMichelin:
			'bg-restaurant-michelin text-neutral-weakest border-restaurant-michelin-strong border-1 border-solid',
		tourism: 'bg-tourism text-neutral-weakest border-tourism-strong border-1 border-solid',
		electric: '',
		rest: '',
	};

	const badgeText: Record<DatasheetPoiScheme, () => string | undefined> = {
		hotel: () => undefined,
		hotelMichelin: m.poi_card__michelin,
		restaurant: () => undefined,
		restaurantMichelin: m.poi_card__michelin,
		tourism: m.poi_card__michelin,
		electric: () => undefined,
		rest: () => undefined,
	};

	const badgeIconName: Record<DatasheetPoiScheme, string> = {
		hotel: '',
		hotelMichelin: 'recommended',
		restaurant: '',
		restaurantMichelin: 'recommended',
		tourism: 'recommended',
		electric: '',
		rest: '',
	};
</script>

<div class="flex gap-4 p-2 rd-2 w-full {backgroundColor[scheme]}" data-testId="datasheet-summary">
	{#if displayPicture && imageUrl}
		<div
			class="md:hidden min-h-18"
			transition:slide={{ duration: 300, easing: cubicInOut, axis: 'x' }}
		>
			<Image
				alt={m.poi_datasheet__image_alt()}
				class="rd-2"
				src={imageUrl}
				width="72px"
				height="100%"
			/>
		</div>
	{/if}
	<div class="flex flex-col min-w-0 justify-center">
		{#if scheme === 'restaurantMichelin' || scheme === 'hotelMichelin' || scheme === 'tourism'}
			<Badge
				colorClasses={badgeColorClasses[scheme]}
				icon={badgeIconName[scheme]}
				text={badgeText[scheme]()}
				iconClasses="text-4"
				iconPosition="right"
				isSquare
				size="sm"
			/>
		{/if}
		<div class="flex flex-col text-coloredneutral min-w-0">
			{#if michelinDistinction}
				<div class="flex flex-row items-center h-6">
					<RepeatIcons
						icon={michelinDistinction.icon}
						amount={michelinDistinction.amount}
						class="{colorClass[scheme]} text-5"
						style="distinction"
					/>
					<div class="ml-1 truncate">{michelinDistinction.label}</div>
				</div>
			{/if}
			{#if hasGreenStar}
				<div class="flex flex-row items-center h-6">
					<Icon icon="green-star" class="text-green-star mr-1" />
					<div class="ml-1 truncate">{m.restaurantMichelin_greenStarLabel()}</div>
				</div>
			{/if}
			{#if reviewText}
				<div class="truncate h-6" data-testId="datasheet-review">{reviewText}</div>
			{/if}
			{#if subTypeText}
				<div class="truncate h-6" data-testId="datasheet-subtype">{subTypeText}</div>
			{/if}
			{#if priceText}
				<div class="truncate h-6" data-testId="datasheet-price">{priceText}</div>
			{:else if priceCategory}
				<div class="flex flex-row items-center h-6 text-5">
					<PriceRange
						length={MAX_PRICE_CATEGORY}
						filledLength={priceCategory.count}
						icon={`currency-${priceCategory.currency}`}
						colorForFilled="text-coloredneutral"
						colorForUnFilled="text-coloredneutral-weak"
					/>
				</div>
			{:else if scheme === 'hotel' || scheme === 'hotelMichelin'}
				<p class="m-0">{m.poi_card__price_unavailable()}</p>
			{/if}
			{#if evData}
				<div class="flex flex-col gap-1">
					<div class="truncate h-6">{evData.spotCount}</div>
					<div class="truncate h-6">{evData.connectorHighlight}</div>
					<div>Max <span class="text-bold">{evData.maxPower.toFixed(2)} kW</span></div>
				</div>
			{/if}
		</div>
	</div>
</div>
